import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, Download, Eye, Printer, Pencil, Plus, Trash2, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ArrowUpDown, Receipt, Camera, ImagePlus, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, formatApiError, fmtIDR, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel, StatusBadge } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { CustomerSearchSelect } from "../components/CustomerSearchSelect";
import { TransferProofThumbnail } from "../components/TransferProofThumbnail";
import {
  getPrinterConfig,
  getReceiptConfig,
  isAndroidDevice,
  printReceipt,
  receiptHtml,
  receiptText,
  saveReceiptConfig,
} from "../lib/qz";

const STATUSES = ["all", "Draft", "Confirmed", "Allocated", "Picking", "Picked", "Packing", "Packed", "Shipped", "Completed", "Cancelled"];
const PAYMENTS = ["all", "Paid", "Partial", "Unpaid"];
const SOURCES = ["all", "WMS", "LEGACY_POS"];
const BUCKETS = ["all", "HOME", "STORE", "PRE_ORDER"];

function printSale(s) { printReceipt(s); }

const KILAT_PHASE_LABEL = { reserved: "Reservasi", ordered: "Pesanan" };
function KilatBadge({ s, className = "" }) {
  if (!s?.is_kilat) return null;
  const phase = KILAT_PHASE_LABEL[s.kilat_phase] || "";
  return (
    <span title="Pesanan Kilat Shopee (FBS) — stok di Gudang Shopee" data-testid={`sale-kilat-badge-${s.id}`}
      className={`inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-1.5 py-0.5 text-[10px] font-semibold ${className}`}>
      <Zap className="w-3 h-3" /> Kilat{phase ? ` · ${phase}` : ""}
    </span>
  );
}
function KilatIds({ s }) {
  if (!s?.is_kilat) return <>{s.order_id || "-"}</>;
  const hasBoth = s.reservation_id && s.order_id && s.reservation_id !== s.order_id;
  return (
    <div className="space-y-0.5" data-testid={`sale-kilat-ids-${s.id}`}>
      {hasBoth ? (
        <>
          <div><span className="text-cocoa/60">Pesanan:</span> {s.order_id}</div>
          <div><span className="text-cocoa/60">Reservasi:</span> {s.reservation_id}</div>
        </>
      ) : (
        <div><span className="text-cocoa/60">Reservasi:</span> {s.reservation_id || s.order_id || "-"}</div>
      )}
    </div>
  );
}

const wibToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta" }).format(new Date());

function ProofCell({ sale, write, onUploaded }) {
  const ref = React.useRef();
  const [uploading, setUploading] = useState(false);
  const available = Boolean(sale.transfer_proof && !sale.transfer_proof.is_deleted);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/cashier/sale/${sale.id}/transfer-proof`, form);
      toast.success("Bukti transfer tersimpan");
      onUploaded();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  if (available) {
    return (
      <div className="flex justify-center">
        <TransferProofThumbnail available saleId={sale.id} testId={`sale-transfer-proof-${sale.id}`} />
      </div>
    );
  }
  if (!write) return <span className="text-xs text-wmuted" data-testid={`sale-transfer-proof-${sale.id}`}>—</span>;
  return (
    <div className="flex justify-center">
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid={`sale-proof-upload-input-${sale.id}`} />
      <Button size="sm" variant="outline" className="border-taupe text-cocoa h-8" disabled={uploading} onClick={() => ref.current?.click()} data-testid={`sale-proof-upload-${sale.id}`}>
        <ImagePlus className="w-3.5 h-3.5 mr-1" />{uploading ? "..." : "Upload"}
      </Button>
    </div>
  );
}

export default function Sales() {
  const { can, canEdit, canDelete } = useAuth();
  const canWriteSale = can("owner", "warehouse_admin", "sales");
  const editResi = async (s) => {
    const val = window.prompt("No. Resi pengiriman:", s.tracking_no || "");
    if (val === null) return;
    try {
      await api.put(`/cashier/sale/${s.id}/tracking`, { tracking_no: val.trim() });
      toast.success("No. Resi diperbarui");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 0, limit: 25 });
  const [f, setF] = useState({ q: "", status: "all", payment_status: "all", source: "all", bucket: "all", missing_proof: false, start: wibToday(), end: wibToday() });
  const [qInput, setQInput] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [detail, setDetail] = useState(null);
  const [editId, setEditId] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const write = can("warehouse_admin", "sales") || canEdit("sales_list");

  const params = useCallback(() => ({
    q: f.q || undefined, status: f.status, payment_status: f.payment_status, source: f.source, bucket: f.bucket,
    missing_proof: f.missing_proof || undefined,
    start: f.start || undefined, end: f.end || undefined, sort_by: sortBy, sort_dir: sortDir,
  }), [f, sortBy, sortDir]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/sales", { params: { ...params(), page, limit } })
      .then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [params, page, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/settings/receipt").then((r) => saveReceiptConfig(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (location.state?.editSaleId) {
      setEditId(location.state.editSaleId);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const applySearch = () => { setF({ ...f, q: qInput }); setPage(1); };
  const setFilter = (k, v) => { setF({ ...f, [k]: v }); setPage(1); };
  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir(col === "created_at" || col === "grand_total" ? "desc" : "asc"); }
    setPage(1);
  };
  const SortTh = ({ col, children, className = "" }) => (
    <TableHead className={`cursor-pointer select-none whitespace-nowrap ${className}`} onClick={() => toggleSort(col)} data-testid={`sort-${col}`}>
      <span className={`inline-flex items-center gap-1 ${className.includes("text-right") ? "justify-end w-full" : className.includes("text-center") ? "justify-center w-full" : ""}`}>
        {children}
        {sortBy === col ? (sortDir === "asc" ? <ChevronUp className="w-3.5 h-3.5 text-cocoa" /> : <ChevronDown className="w-3.5 h-3.5 text-cocoa" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </span>
    </TableHead>
  );
  const doPrint = async (id) => {
    try { const r = await api.get(`/sales/${id}`); printReceipt(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async (s) => {
    if (!window.confirm(`Hapus penjualan ${s.number}? Stok yang terjual dikembalikan ke available, lalu data dihapus permanen. Aksi ini tidak bisa dibatalkan.`)) return;
    try { await api.delete(`/sales/${s.id}`); toast.success(`Penjualan ${s.number} dihapus`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="sales-page">
      <PageHeader title="Daftar Penjualan" subtitle="Riwayat & manajemen penjualan (termasuk data legacy)">
        {write && <Button onClick={() => navigate("/sales/create")} className="bg-cocoa hover:bg-cocoa-dark text-ivory mr-2" data-testid="create-sales-btn"><Plus className="w-4 h-4 mr-1.5" /> Create Sales</Button>}
        <Button onClick={() => setExportOpen(true)} variant="outline" className="border-taupe text-cocoa" data-testid="export-sales-btn"><Download className="w-4 h-4 mr-1.5" /> Export Excel</Button>
      </PageHeader>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
          <Input value={qInput} onChange={(e) => setQInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applySearch()} placeholder="Cari no. sales / customer / order id..." className="pl-9 bg-white" data-testid="sales-search" />
        </div>
        <Button variant="outline" onClick={applySearch} className="border-taupe text-cocoa">Cari</Button>
        <Select value={f.status} onValueChange={(v) => setFilter("status", v)}>
          <SelectTrigger className="w-40 bg-white" data-testid="sales-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "Semua Status" : s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.payment_status} onValueChange={(v) => setFilter("payment_status", v)}>
          <SelectTrigger className="w-36 bg-white" data-testid="sales-payment-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{PAYMENTS.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "Semua Bayar" : s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.source} onValueChange={(v) => setFilter("source", v)}>
          <SelectTrigger className="w-36 bg-white" data-testid="sales-source-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "Semua Sumber" : (s === "LEGACY_POS" ? "Legacy" : s)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.bucket} onValueChange={(v) => setFilter("bucket", v)}>
          <SelectTrigger className="w-40 bg-white" data-testid="sales-bucket-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{BUCKETS.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "Semua Bucket" : s}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={f.start} onChange={(e) => setFilter("start", e.target.value)} className="w-40 bg-white" data-testid="sales-start-date" />
        <Input type="date" value={f.end} onChange={(e) => setFilter("end", e.target.value)} className="w-40 bg-white" data-testid="sales-end-date" />
        <Button variant="outline" onClick={() => { setFilter("start", wibToday()); setF((p) => ({ ...p, start: wibToday(), end: wibToday() })); setPage(1); }} className="border-taupe text-cocoa" data-testid="sales-today-btn">Hari Ini</Button>
        {(f.start || f.end) && <Button variant="ghost" onClick={() => { setF((p) => ({ ...p, start: "", end: "" })); setPage(1); }} className="text-wmuted" data-testid="sales-alldates-btn">Semua Tanggal</Button>}
        <Button variant={f.missing_proof ? "default" : "outline"} onClick={() => setFilter("missing_proof", !f.missing_proof)} className={f.missing_proof ? "bg-cocoa text-ivory" : "border-taupe text-cocoa"} data-testid="sales-missing-proof-filter" title="Transaksi non-cash (selain Cash) yang belum ada bukti transfer, di luar marketplace">Belum Ada Bukti TF</Button>
      </div>

      <Panel className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-12">No</TableHead>
              <SortTh col="number">No. Sales</SortTh>
              <SortTh col="created_at">Tanggal</SortTh>
              <SortTh col="customer_name">Customer</SortTh>
              <SortTh col="keep_number">No. Keep</SortTh>
              <SortTh col="order_id">Order ID</SortTh>
              <TableHead>Resi</TableHead>
              <SortTh col="total_items" className="text-center">Total Item</SortTh>
              <SortTh col="group_name">Group</SortTh>
              <SortTh col="payment_method">Payment Type</SortTh>
              <TableHead className="text-center">Crosscheck</TableHead>
              <TableHead className="text-right">Ongkir</TableHead>
              <SortTh col="grand_total" className="text-right">Total Sales</SortTh>
              <TableHead className="text-center">Bukti</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((s, i) => {
              const isResellerKeep = String(s.group_name || "").toLowerCase() === "reseller";
              const isLegacyDirect = s.source === "LEGACY_POS" && !s.keep_id;
              const canEdit = write;
              return (
                <TableRow key={s.id} data-testid={`sale-row-${s.id}`}>
                  <TableCell className="text-wmuted">{(data.page - 1) * data.limit + i + 1}</TableCell>
                  <TableCell className="font-mono text-sm font-semibold text-cocoa">
                    <div>{s.number}</div>
                    {s.is_kilat && <KilatBadge s={s} className="mt-0.5" />}
                  </TableCell>
                  <TableCell className="text-sm">{fmtDate(s.created_at)}</TableCell>
                  <TableCell>{s.customer_name || "-"}</TableCell>
                  <TableCell className="font-mono text-xs">{s.keep_number || "-"}</TableCell>
                  <TableCell className="text-xs text-wmuted">{s.is_kilat ? <KilatIds s={s} /> : (s.order_id || "-")}</TableCell>
                  <TableCell className="text-xs">
                    {s.tracking_no ? (
                      <div className="flex items-center gap-1">
                        <a href={`https://cekresi.com/?noresi=${encodeURIComponent(s.tracking_no)}`} target="_blank" rel="noreferrer" className="font-mono text-blue-600 hover:underline" data-testid={`sale-track-link-${s.id}`} title="Cek resi di cekresi.com">{s.tracking_no}</a>
                        {canWriteSale && <button type="button" onClick={() => editResi(s)} data-testid={`sale-track-edit-${s.id}`} className="text-cocoa/40 hover:text-cocoa"><Pencil className="w-3 h-3" /></button>}
                      </div>
                    ) : (canWriteSale ? <button type="button" onClick={() => editResi(s)} data-testid={`sale-track-add-${s.id}`} className="text-blue-600 hover:underline">+ Resi</button> : "-")}
                  </TableCell>
                  <TableCell className="text-center">{s.total_items || 0}</TableCell>
                  <TableCell className="text-sm">{s.group_name || "-"}</TableCell>
                  <TableCell className="text-sm">{s.payment_method || "-"}</TableCell>
                  <TableCell className="text-center">
                    {s.shipping_reconciliation_status === "needs_review" ? (
                      <div data-testid={`sale-shipping-review-${s.id}`}>
                        <div className="text-xs font-semibold text-status-reject">Perlu Ditinjau</div>
                        {s.shipping_reconciliation_reason === "flash_order" && <div className="text-[10px] text-cocoa">Pesanan Kilat · Stok Shopee</div>}
                        {s.shipping_reconciliation_reason === "missing_shipping_file" && <div className="text-[10px] text-wmuted">Di luar file pengiriman</div>}
                      </div>
                    ) : s.shipping_reconciliation_status === "handled" ? (
                      <span className="text-xs font-semibold text-status-passed" data-testid={`sale-shipping-handled-${s.id}`}>
                        Ditangani
                      </span>
                    ) : s.shipping_reconciliation_status === "outside_marketplace" ? (
                      <span className="text-xs font-semibold text-cocoa" data-testid={`sale-outside-marketplace-${s.id}`}>
                        Luar Marketplace
                      </span>
                    ) : <span className="text-xs text-wmuted" data-testid={`sale-shipping-none-${s.id}`}>—</span>}
                  </TableCell>
                  <TableCell className="text-right">{fmtIDR(s.shipping_cost)}</TableCell>
                  <TableCell className="text-right font-medium">{fmtIDR(s.grand_total)}</TableCell>
                  <TableCell className="text-center">
                    <ProofCell sale={s} write={write} onUploaded={load} />
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" title="Show Detail" onClick={() => setDetail(s.id)} className="text-cocoa h-8 w-8" data-testid={`sale-detail-${s.id}`}><Eye className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" title="Preview / Print Nota" onClick={() => setPreviewId(s.id)} className="text-cocoa h-8 w-8" data-testid={`sale-print-${s.id}`}><Printer className="w-4 h-4" /></Button>
                    {canEdit && <Button size="icon" variant="ghost" title="Edit Sales" onClick={() => setEditId(s.id)} className="text-blue-600 h-8 w-8" data-testid={`sale-edit-${s.id}`}><Pencil className="w-4 h-4" /></Button>}
                    {write && canDelete("sales_list") && <Button size="icon" variant="ghost" title="Hapus Sales (stok dikembalikan)" onClick={() => doDelete(s)} className="text-status-reject h-8 w-8" data-testid={`sale-delete-${s.id}`}><Trash2 className="w-4 h-4" /></Button>}
                  </TableCell>
                </TableRow>
              );
            })}
            {!data.items.length && <TableRow><TableCell colSpan={15} className="text-center text-wmuted py-10"><Receipt className="w-6 h-6 mx-auto mb-2 opacity-50" />{loading ? "Memuat..." : "Tidak ada penjualan."}</TableCell></TableRow>}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between px-4 py-3 border-t border-taupe/15 text-sm">
          <div className="flex items-center gap-3">
            <span className="text-wmuted">Tampilkan</span>
            <Select value={String(limit)} onValueChange={(v) => { setLimit(parseInt(v)); setPage(1); }}>
              <SelectTrigger className="w-20 h-8 bg-white" data-testid="sales-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>{[10, 20, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-wmuted">
              {data.total ? `${(data.page - 1) * data.limit + 1}–${Math.min(data.page * data.limit, data.total)} dari ${data.total.toLocaleString("id-ID")}` : "0 data"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-wmuted">Halaman {data.page}/{data.pages || 1}</span>
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="border-taupe text-cocoa" data-testid="sales-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <Button size="sm" variant="outline" disabled={page >= (data.pages || 1)} onClick={() => setPage(page + 1)} className="border-taupe text-cocoa" data-testid="sales-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </Panel>

      <SaleDetail sid={detail} setSid={setDetail} />
      <ReceiptPreview sid={previewId} setSid={setPreviewId} />
      <EditSaleDialog sid={editId} setSid={setEditId} onDone={load} />
      <SalesExportDialog open={exportOpen} setOpen={setExportOpen} />
    </div>
  );
}

function ReceiptPreview({ sid, setSid }) {
  const [sale, setSale] = useState(null);
  useEffect(() => {
    if (!sid) { setSale(null); return; }
    setSale(null);
    api.get(`/sales/${sid}`).then((r) => setSale(r.data)).catch(() => {});
  }, [sid]);
  const printer = getPrinterConfig() || {};
  const receipt = getReceiptConfig() || {};
  const width = getReceiptConfig()?.paper_width || 58;
  const px = Math.round(width * 3.78);
  const useRawBtPreview = (
    (printer.method || receipt.print_method) === "rawbt"
    && isAndroidDevice()
  );
  return (
    <Dialog open={!!sid} onOpenChange={(v) => !v && setSid(null)}>
      <DialogContent className="max-w-md" data-testid="receipt-preview-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Preview Nota {sale?.number || ""}</DialogTitle></DialogHeader>
        {!sale ? <div className="py-8 text-center text-wmuted">Memuat...</div> : (
          <>
            <div className="flex justify-center bg-taupe/10 p-4 rounded-lg max-h-[62vh] overflow-auto">
              {useRawBtPreview ? (
                <pre
                  className="w-full max-w-[360px] bg-white p-3 text-[11px] leading-tight text-black"
                  data-testid="receipt-preview-rawbt"
                >
                  {receiptText(sale, width >= 80 ? 48 : 32)}
                </pre>
              ) : (
                <iframe
                  title="preview-nota"
                  srcDoc={receiptHtml(sale, width)}
                  style={{
                    width: `${px}px`,
                    height: "58vh",
                    border: "1px solid #e5ddd3",
                    background: "#fff",
                  }}
                  data-testid="receipt-preview-frame"
                />
              )}
            </div>
            <p className="text-xs text-wmuted mt-2">
              {useRawBtPreview
                ? `Format teks yang dikirim ke RawBT (${width} mm).`
                : `Tampilan dialog browser untuk kertas ${width} mm.`}
              {" Untuk ubah teks/header/footer buka "}
              <b>Ventedaily → Printer → Kustom Teks Struk</b>.
            </p>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setSid(null)} className="border-taupe text-cocoa">Tutup</Button>
          <Button onClick={() => sale && printReceipt(sale)} disabled={!sale} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="receipt-preview-print"><Printer className="w-4 h-4 mr-1.5" />Print</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SaleDetail({ sid, setSid }) {
  const [sale, setSale] = useState(null);
  const [err, setErr] = useState(false);
  const { can, canEdit } = useAuth();
  const canUploadProof = can("warehouse_admin", "sales") || canEdit("sales_list");
  useEffect(() => {
    if (!sid) { setSale(null); setErr(false); return; }
    setSale(null); setErr(false);
    api.get(`/sales/${sid}`).then((r) => setSale(r.data)).catch(() => setErr(true));
  }, [sid]);

  return (
    <Dialog open={!!sid} onOpenChange={(v) => !v && setSid(null)}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa flex items-center justify-between pr-6">Sales {sale?.number || ""}{sale && <Button size="sm" variant="outline" onClick={() => printSale(sale)} className="border-taupe text-cocoa" data-testid="sale-detail-print"><Printer className="w-4 h-4 mr-1.5" />Print</Button>}</DialogTitle></DialogHeader>
        {err ? <div className="py-6 text-center text-status-reject">Gagal memuat detail. Coba lagi.</div> : !sale ? <div className="py-6 text-center text-wmuted">Memuat...</div> : (
          <>
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <div><span className="text-wmuted">Customer:</span> {sale.customer_name || "-"}</div>
              <div><span className="text-wmuted">Tanggal:</span> {fmtDate(sale.created_at)}</div>
              <div><span className="text-wmuted">No. Keep:</span> {sale.keep_number || "-"}</div>
              <div className="flex items-center gap-1.5"><span className="text-wmuted">Order ID:</span> {sale.order_id || "-"}<KilatBadge s={sale} /></div>
              {sale.is_kilat && sale.reservation_id && sale.reservation_id !== sale.order_id && (
                <div data-testid="sale-detail-reservation-id"><span className="text-wmuted">ID Reservasi:</span> <span className="font-mono text-xs">{sale.reservation_id}</span></div>
              )}
              <div><span className="text-wmuted">Group:</span> {sale.group_name || "-"}</div>
              <div><span className="text-wmuted">Payment Type:</span> {sale.payment_method || "-"}</div>
              <div><span className="text-wmuted">Ongkos Kirim:</span> {fmtIDR(sale.shipping_cost)}</div>
              <div><span className="text-wmuted">Status:</span> <StatusBadge status={sale.status} /></div>
              <div><span className="text-wmuted">Sumber:</span> {sale.source === "LEGACY_POS" ? "Legacy" : "WMS"}</div>
            </div>
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Produk</TableHead><TableHead>Varian</TableHead><TableHead>Gudang</TableHead><TableHead className="text-center">Qty</TableHead><TableHead className="text-right">Harga</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
              <TableBody>
                {(sale.items || []).map((it, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-sm">{it.product_name || <span className="text-wmuted italic">(varian arsip)</span>}</TableCell>
                    <TableCell className="text-sm">{it.color}/{it.size}</TableCell>
                    <TableCell className="text-sm">{it.warehouse_name || "-"}</TableCell>
                    <TableCell className="text-center">{it.qty}</TableCell>
                    <TableCell className="text-right">{fmtIDR(it.price)}</TableCell>
                    <TableCell className="text-right">{fmtIDR(it.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="mt-3 space-y-1 text-sm max-w-xs ml-auto">
              <div className="flex justify-between"><span className="text-wmuted">Subtotal</span><span>{fmtIDR(sale.sub_total)}</span></div>
              {sale.discount ? <div className="flex justify-between"><span className="text-wmuted">Diskon</span><span>-{fmtIDR(sale.discount)}</span></div> : null}
              {sale.shipping_cost ? <div className="flex justify-between"><span className="text-wmuted">Ongkos Kirim</span><span>{fmtIDR(sale.shipping_cost)}</span></div> : null}
              <div className="flex justify-between font-bold text-cocoa"><span>Grand Total</span><span>{fmtIDR(sale.grand_total)}</span></div>
              <div className="flex justify-between"><span className="text-wmuted">Dibayar</span><span>{fmtIDR(sale.paid)}</span></div>
              {sale.outstanding ? <div className="flex justify-between text-status-reject"><span>Sisa</span><span>{fmtIDR(sale.outstanding)}</span></div> : null}
            </div>
            <TransferProofManager
              canUpload={canUploadProof}
              sale={sale}
              setSale={setSale}
            />
            {(sale.edit_history || []).length > 0 && (
              <div className="mt-4 border-t border-taupe/20 pt-3" data-testid="sale-edit-history">
                <div className="text-sm font-semibold text-cocoa mb-1.5">Riwayat Edit</div>
                <div className="space-y-1">
                  {(sale.edit_history || []).map((h, i) => (
                    <div key={i} className="text-xs text-wmuted flex flex-wrap gap-x-2">
                      <span className="text-cocoa font-medium">{h.by || "-"}</span>
                      <span>· {fmtDate(h.at)}</span>
                      <span>· item {h.before?.total_items ?? "-"}→{h.after?.total_items ?? "-"}</span>
                      <span>· total {fmtIDR(h.before?.grand_total)}→{fmtIDR(h.after?.grand_total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TransferProofManager({ canUpload, sale, setSale }) {
  const cameraRef = React.useRef();
  const galleryRef = React.useRef();
  const [uploading, setUploading] = useState(false);
  const available = Boolean(sale.transfer_proof && !sale.transfer_proof.is_deleted);

  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const response = await api.post(`/cashier/sale/${sale.id}/transfer-proof`, form);
      setSale((current) => ({ ...current, transfer_proof: response.data.proof }));
      toast.success("Bukti transfer tersimpan");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  return (
    <div className="mt-4 border-t border-taupe/20 pt-3" data-testid="sale-proof-manager">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <div className="text-sm font-semibold text-cocoa">Bukti Transfer</div>
          <div className="text-xs text-wmuted" data-testid="sale-proof-status">
            {available ? sale.transfer_proof.original_filename || "Tersedia" : "Belum ada bukti"}
          </div>
        </div>
        {available && (
          <TransferProofThumbnail
            available={available}
            saleId={sale.id}
            testId="sale-detail-transfer-proof-thumbnail"
          />
        )}
        {canUpload && (
          <div className="ml-auto flex flex-wrap gap-2">
            <input
              ref={cameraRef}
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              className="hidden"
              data-testid="sale-detail-transfer-proof-camera-input"
              onChange={upload}
              type="file"
            />
            <input
              ref={galleryRef}
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              data-testid="sale-detail-transfer-proof-gallery-input"
              onChange={upload}
              type="file"
            />
            <Button
              data-testid="sale-detail-transfer-proof-camera-button"
              disabled={uploading}
              onClick={() => cameraRef.current?.click()}
              size="sm"
              type="button"
              variant="outline"
            >
              <Camera className="mr-1 h-4 w-4" />
              Kamera
            </Button>
            <Button
              data-testid="sale-detail-transfer-proof-gallery-button"
              disabled={uploading}
              onClick={() => galleryRef.current?.click()}
              size="sm"
              type="button"
              variant="outline"
            >
              <ImagePlus className="mr-1 h-4 w-4" />
              {uploading ? "Mengunggah..." : "Galeri"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function EditSaleDialog({ sid, setSid, onDone }) {
  const [sale, setSale] = useState(null);
  const [rows, setRows] = useState([]);
  const [selCust, setSelCust] = useState(null);
  const [methods, setMethods] = useState([]);
  const [payMethod, setPayMethod] = useState("Cash");
  const [cash, setCash] = useState("");
  const [discType, setDiscType] = useState("none");
  const [discValue, setDiscValue] = useState("");
  const [note, setNote] = useState("");
  const [orderId, setOrderId] = useState("");
  const [saving, setSaving] = useState(false);
  const [addQ, setAddQ] = useState("");
  const [addResults, setAddResults] = useState([]);

  useEffect(() => {
    if (!sid) { setSale(null); setRows([]); return; }
    api.get(`/sales/${sid}`).then((r) => {
      const s = r.data; setSale(s);
      setRows((s.items || []).map((it) => ({
        variant_id: it.variant_id,
        product_name: it.product_name,
        color: it.color,
        size: it.size,
        price: it.price,
        qty: it.qty,
        warehouse_id: it.warehouse_id,
        warehouse_name: it.warehouse_name,
      })));
      setSelCust(s.customer_id ? { id: s.customer_id, name: s.customer_name, phone: "" } : null);
      setOrderId(s.order_id || "");
      setPayMethod(s.payment_method || "Cash"); setCash(String(s.cash_received || ""));
      setDiscType(s.discount_type || "none"); setDiscValue(String(s.discount_value || ""));
      setNote(s.note || "");
    }).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); setSid(null); });
    api.get("/payment-methods", { params: { active: "active" } }).then((r) => setMethods(r.data)).catch(() => {});
  }, [sid]);

  useEffect(() => {
    const term = addQ.trim();
    if (term.length < 2) { setAddResults([]); return; }
    const t = setTimeout(() => {
      api.get("/keep/products", { params: { q: term } }).then((r) => {
        const out = [];
        (r.data.products || []).forEach((p) => (p.variants || []).forEach((v) => out.push({
          variant_id: v.variant_id, product_name: p.product_name, color: v.color, size: v.size, price: v.price,
          total: (v.warehouses || []).reduce((s, w) => s + (w.available || 0), 0),
        })));
        setAddResults(out.slice(0, 40));
      }).catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [addQ]);

  const subtotal = rows.reduce((s, r) => s + (parseInt(r.price) || 0) * (parseInt(r.qty) || 0), 0);
  const discountAmt = discType === "percent" ? Math.min(Math.round(subtotal * Math.min(parseInt(discValue) || 0, 100) / 100), subtotal) : discType === "amount" ? Math.min(parseInt(discValue) || 0, subtotal) : 0;
  const grandTotal = subtotal - discountAmt;

  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addVariant = (v) => {
    setRows((rs) => rs.some((r) => r.variant_id === v.variant_id) ? rs.map((r) => r.variant_id === v.variant_id ? { ...r, qty: (parseInt(r.qty) || 0) + 1 } : r) : [...rs, { variant_id: v.variant_id, product_name: v.product_name, color: v.color, size: v.size, price: v.price, qty: 1 }]);
    setAddQ(""); setAddResults([]);
  };

  const save = async () => {
    if (saving) return;
    const items = rows.filter((r) => r.variant_id && parseInt(r.qty) > 0).map((r) => ({
      variant_id: r.variant_id,
      qty: parseInt(r.qty) || 0,
      price: parseInt(r.price) || 0,
      warehouse_id: r.warehouse_id || null,
    }));
    if (!items.length) return toast.error("Minimal satu item");
    const metaOnly = !!(sale.keep_id && String(sale.group_name || "").toLowerCase() !== "reseller");
    const confirmMsg = metaOnly
      ? "Simpan perubahan data transaksi (Order ID, customer, pembayaran, catatan)? Item & stok TIDAK diubah untuk sales dari Keep."
      : "Edit penjualan akan menyesuaikan stok (mengembalikan stok lama & memotong stok baru). Lanjutkan?";
    if (!window.confirm(confirmMsg)) return;
    const payload = {
      customer_id: selCust?.id || null, customer_name: selCust?.name || "",
      group_id: sale.group_id || null, group_name: sale.group_name || "", sales_bucket: sale.sales_bucket || "HOME",
      term_of_payment: sale.term_of_payment || "Cash", note, items, order_id: orderId,
      discount_type: discType, discount_value: parseInt(discValue) || 0,
      payment_method: payMethod, cash_received: parseInt(cash) || 0,
    };
    setSaving(true);
    try { await api.put(`/cashier/sale/${sid}`, payload); toast.success(`Penjualan ${sale.number} diperbarui`); setSid(null); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!sid} onOpenChange={(v) => !v && setSid(null)}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-sale-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Edit Sales {sale?.number || ""}</DialogTitle></DialogHeader>
        {!sale ? <div className="py-6 text-center text-wmuted">Memuat...</div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Customer</Label>
                <CustomerSearchSelect groupId={sale.group_id} selected={selCust} testid="edit-sale-customer"
                  onSelect={(c) => setSelCust(c)} onClear={() => setSelCust(null)} />
              </div>
              <div><Label>Group</Label><Input value={sale.group_name || "-"} disabled className="bg-taupe/10" /></div>
              <div><Label>Order ID (Marketplace)</Label><Input value={orderId} onChange={(e) => setOrderId(e.target.value)} className="bg-white" placeholder="(opsional) — biarkan jika tak berubah" data-testid="edit-sale-orderid" /></div>
            </div>
            {sale.keep_id && (
              <div className="text-xs text-wmuted" data-testid="edit-reseller-keep-notice">
                Item Reseller berasal dari Keep. Varian baru tidak dapat ditambahkan; qty dapat
                disesuaikan sesuai stok Keep asal.
              </div>
            )}
            {sale.source === "LEGACY_POS" && !sale.keep_id && (
              <div className="text-xs text-wmuted" data-testid="edit-legacy-sale-notice">
                Data legacy tidak menyimpan gudang asal. Bila qty berubah, hanya selisih stok
                disesuaikan dari Gudang Utama.
              </div>
            )}

            {sale.keep_id && String(sale.group_name || "").toLowerCase() !== "reseller" && (
              <div className="text-xs text-amber-700" data-testid="edit-marketplace-keep-notice">
                Sales dari Keep marketplace: perubahan item/qty <b>diabaikan</b>. Hanya Order ID, customer, pembayaran, diskon, & catatan yang disimpan.
              </div>
            )}

            <Panel>
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Produk</TableHead><TableHead>Varian</TableHead><TableHead className="text-right">Harga</TableHead><TableHead className="text-center w-24">Qty</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={`${r.variant_id}-${i}`} data-testid={`edit-sale-row-${i}`}>
                      <TableCell className="text-sm font-medium text-cocoa">{r.product_name}</TableCell>
                      <TableCell className="text-sm">{r.color}/{r.size}</TableCell>
                      <TableCell className="text-right"><Input type="number" className="w-28 text-right ml-auto" value={r.price} onChange={(e) => setRow(i, { price: e.target.value })} data-testid={`edit-sale-price-${i}`} /></TableCell>
                      <TableCell className="text-center"><Input type="number" className="w-20 text-center mx-auto" value={r.qty} onChange={(e) => setRow(i, { qty: e.target.value })} data-testid={`edit-sale-qty-${i}`} /></TableCell>
                      <TableCell className="text-right font-medium">{fmtIDR((parseInt(r.price) || 0) * (parseInt(r.qty) || 0))}</TableCell>
                      <TableCell><Button size="icon" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`edit-sale-del-${i}`}><Trash2 className="w-4 h-4 text-status-reject" /></Button></TableCell>
                    </TableRow>
                  ))}
                  {!rows.length && <TableRow><TableCell colSpan={6} className="text-center text-wmuted py-6">Belum ada item.</TableCell></TableRow>}
                </TableBody>
              </Table>
              {!sale.keep_id && <div className="p-3 border-t border-taupe/15">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
                  <Input value={addQ} onChange={(e) => setAddQ(e.target.value)} placeholder="Tambah produk — ketik nama (min 2 huruf)..." className="pl-9 bg-white" data-testid="edit-sale-add-search" />
                </div>
                {addResults.length > 0 && (
                  <div className="mt-1 border border-taupe/20 rounded-md max-h-56 overflow-y-auto">
                    {addResults.map((v) => (
                      <button key={v.variant_id} onClick={() => addVariant(v)} data-testid={`edit-sale-add-${v.variant_id}`} className="flex w-full items-center gap-2 px-3 py-2 hover:bg-ivory text-sm border-b border-taupe/10 last:border-0 text-left">
                        <Plus className="w-3.5 h-3.5 text-blue-600" />
                        <span className="font-medium text-cocoa">{v.product_name}</span>
                        <span className="text-wmuted">{v.color}/{v.size}</span>
                        <span className="text-wmuted text-xs ml-auto">stok {v.total} · {fmtIDR(v.price)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>}
            </Panel>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label>Diskon</Label>
                <div className="flex gap-2">
                  <Select value={discType} onValueChange={setDiscType}><SelectTrigger className="w-28 bg-white" data-testid="edit-sale-disc-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Tanpa</SelectItem><SelectItem value="amount">Rp</SelectItem><SelectItem value="percent">%</SelectItem></SelectContent></Select>
                  <Input type="number" value={discValue} onChange={(e) => setDiscValue(e.target.value)} disabled={discType === "none"} className="bg-white" placeholder="0" data-testid="edit-sale-disc-value" />
                </div>
              </div>
              <div><Label>Payment Type</Label>
                <Select value={payMethod || undefined} onValueChange={setPayMethod}><SelectTrigger className="bg-white" data-testid="edit-sale-pay-method"><SelectValue placeholder="Pilih metode" /></SelectTrigger><SelectContent>{methods.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Cash Received</Label><Input type="number" value={cash} onChange={(e) => setCash(e.target.value)} className="bg-white" data-testid="edit-sale-cash" /></div>
            </div>
            <div><Label>Catatan</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="bg-white" /></div>

            <div className="flex items-center justify-between text-sm">
              <div className="space-y-0.5">
                <div className="flex gap-6"><span className="text-wmuted">Subtotal</span><span>{fmtIDR(subtotal)}</span></div>
                {discountAmt ? <div className="flex gap-6 text-status-reject"><span>Diskon</span><span>- {fmtIDR(discountAmt)}</span></div> : null}
                <div className="flex gap-6 font-bold text-cocoa"><span>Grand Total</span><span data-testid="edit-sale-grandtotal">{fmtIDR(grandTotal)}</span></div>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setSid(null)} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} disabled={saving || !sale} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="edit-sale-save-btn">{saving ? "Menyimpan..." : "Simpan Perubahan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SALES_REPORTS = [
  { value: "sales-rekap-accounting", label: "Sales Rekap Accounting", filter: "group" },
  { value: "rekap-penjualan-produk", label: "Rekap Penjualan Produk", filter: "group" },
  { value: "rekap-transaksi", label: "Rekap Transaksi Penjualan", filter: "group" },
  { value: "rekap-transaksi-produk", label: "Rekap Transaksi By Produk", filter: "product" },
  { value: "rekap-pengiriman-kol", label: "Rekap Pengiriman KOL (per hari)", filter: "group" },
];

const EXPORT_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const fmtLongDate = (s) => {
  if (!s || s.length < 10) return "";
  const [y, m, d] = s.split("-");
  return `${d} ${EXPORT_MONTHS[parseInt(m, 10) - 1] || m} ${y}`;
};

function SalesExportDialog({ open, setOpen }) {
  const [report, setReport] = useState("rekap-penjualan-produk");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [groupId, setGroupId] = useState("all");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [productId, setProductId] = useState("all");
  const [groups, setGroups] = useState([]);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const filterType = (SALES_REPORTS.find((r) => r.value === report) || {}).filter;

  const exportFileName = () => {
    const label = (SALES_REPORTS.find((r) => r.value === report) || {}).label || report;
    let range = "Semua Periode";
    if (startDate && endDate) range = `${fmtLongDate(startDate)} - ${fmtLongDate(endDate)}`;
    else if (startDate) range = `sejak ${fmtLongDate(startDate)}`;
    else if (endDate) range = `s.d ${fmtLongDate(endDate)}`;
    return `${label} ${range}.xlsx`;
  };

  useEffect(() => {
    if (!open) return;
    setSelectedCustomer(null);
    api.get("/customer-groups", { params: { active: "active" } }).then((r) => setGroups(r.data)).catch(() => {});
    api.get("/products", { params: { status: "active" } }).then((r) => setProducts(r.data)).catch(() => {});
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      const params = { report, start: startDate || undefined, end: endDate || undefined };
      if (filterType === "group") params.group_id = groupId === "all" ? undefined : groupId;
      if (filterType === "group") params.customer_id = selectedCustomer?.id || undefined;
      if (filterType === "product") params.product_id = productId === "all" ? undefined : productId;
      await downloadFile("/sales/export", params, exportFileName());
      toast.success("Export Excel berhasil"); setOpen(false);
    } catch (e) { toast.error("Export gagal"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl" data-testid="sales-export-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-cocoa">Export Penjualan</DialogTitle>
          <DialogDescription className="sr-only" data-testid="sales-export-description">
            Pilih tipe laporan, periode, grup customer, dan customer sebelum mengunduh Excel.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-5" data-testid="sales-export-report">
            {SALES_REPORTS.map((rp) => (
              <label key={rp.value} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="sales-report" checked={report === rp.value} onChange={() => setReport(rp.value)} data-testid={`sales-report-${rp.value}`} />
                {rp.label}
              </label>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Start Date</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-white" data-testid="sales-export-start" /></div>
            <div><Label>End Date</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-white" data-testid="sales-export-end" /></div>
          </div>
          {filterType === "group" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Customer Group</Label>
                <Select value={groupId} onValueChange={(value) => {
                  setGroupId(value);
                  setSelectedCustomer(null);
                }}>
                  <SelectTrigger className="bg-white" data-testid="sales-export-group"><SelectValue placeholder="All Group" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Group</SelectItem>
                    {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Customer</Label>
                <CustomerSearchSelect
                  groupId={groupId === "all" ? undefined : groupId}
                  selected={selectedCustomer}
                  onSelect={setSelectedCustomer}
                  onClear={() => setSelectedCustomer(null)}
                  placeholder="All Customer — cari nama / No. telepon..."
                  testid="sales-export-customer"
                />
              </div>
            </div>
          )}
          {filterType === "product" && (
            <div><Label>Product</Label>
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger className="bg-white" data-testid="sales-export-product"><SelectValue placeholder="All Product" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Product</SelectItem>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-status-passed hover:bg-status-passed/90 text-white" data-testid="sales-export-excel-btn"><Download className="w-4 h-4 mr-1.5" />{busy ? "Menyiapkan..." : "Excel"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
